export const TOAST_RAIL_MIN_HORIZONTAL_TRAVEL = 24;
export const TOAST_RAIL_MIN_HORIZONTAL_VELOCITY = 0.5;

function eventTime(event) {
  return Number.isFinite(event && event.timeStamp) ? event.timeStamp : Date.now();
}

function releasePointerCapture(element, pointerId) {
  if (!element || typeof element.releasePointerCapture !== "function") return;

  try {
    if (typeof element.hasPointerCapture !== "function" || element.hasPointerCapture(pointerId)) {
      element.releasePointerCapture(pointerId);
    }
  } catch (_error) {
    // A browser can release capture before a cancellation reaches React.
  }
}

/**
 * Supplements React-Toastify's horizontal distance drag with a short, fast
 * swipe. It intentionally never prevents default or stops propagation, so the
 * library-owned distance path remains in control as well.
 */
export function createToastRailGesture({ canDismiss = true, onDismiss } = {}) {
  if (canDismiss === false || typeof onDismiss !== "function") return {};

  let active = null;
  let dismissed = false;

  function clear(pointerId) {
    if (!active || active.pointerId !== pointerId) return;
    releasePointerCapture(active.element, pointerId);
    active = null;
  }

  // The one place that enforces "close at most once". Keeping it single means a
  // second complete, qualifying gesture actually exercises the guard instead of
  // being turned away earlier by a redundant check.
  function close() {
    if (dismissed) return;
    dismissed = true;
    onDismiss();
  }

  function onPointerDown(event) {
    if (!event || event.isPrimary === false || !Number.isFinite(event.pointerId)) return;
    if (active) {
      if (event.pointerId !== active.pointerId) active.invalid = true;
      return;
    }

    const element = event.currentTarget;
    active = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startedAt: eventTime(event),
      element,
      invalid: false,
    };

    try {
      if (element && typeof element.setPointerCapture === "function") element.setPointerCapture(event.pointerId);
    } catch (_error) {
      // Capture is a safety enhancement; the identity checks still protect us.
    }
  }

  function onPointerMove(event) {
    if (!active || !event || event.pointerId !== active.pointerId) return;
    // The final coordinates and timestamp are evaluated on pointerup. Keeping
    // this handler deliberately inert lets v11 retain its own distance drag.
  }

  function onPointerCancel(event) {
    if (!active || !event || event.pointerId !== active.pointerId) return;
    clear(event.pointerId);
  }

  function onPointerUp(event) {
    if (!active || !event) return;

    if (event.pointerId !== active.pointerId) {
      // A second pointer means this was not an unambiguous one-finger swipe.
      active.invalid = true;
      return;
    }

    const gesture = active;
    clear(event.pointerId);
    if (gesture.invalid) return;

    const horizontalTravel = Math.abs(event.clientX - gesture.startX);
    const verticalTravel = Math.abs(event.clientY - gesture.startY);
    const elapsed = Math.max(eventTime(event) - gesture.startedAt, 1);
    const velocity = horizontalTravel / elapsed;

    if (
      horizontalTravel < TOAST_RAIL_MIN_HORIZONTAL_TRAVEL ||
      velocity < TOAST_RAIL_MIN_HORIZONTAL_VELOCITY ||
      horizontalTravel <= verticalTravel
    ) {
      return;
    }

    close();
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
